/**
 * One reading of `public.menu_context()`, kept up to date with the four
 * answers it depends on, and everything the page draws worked out from it.
 *
 * vB6 recomputes the whole page synchronously on every keystroke, because the
 * catalogue, the register and the calendar were in the file. Here four of the
 * customer's answers — the date, the start time, the postcode and whether
 * they are collecting — change what the server has to be asked, so the
 * reading is fetched again when one of them moves.
 *
 * The one thing that must not happen while it is in flight is the thing §9 of
 * `docs/overnight_constraints.md` asks every stage to look for: a figure on
 * screen that belongs to an answer the customer has already changed. So the
 * page keeps the last good reading and draws from it, `stale` says out loud
 * that it is being brought up to date, and the order button is disabled until
 * it is. Nothing silently shows yesterday's number as though it were today's.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  MenuContextError,
  readMenuContext,
  type MenuContext,
  type MenuContextQuery,
} from '../lib/menuContext.ts'
import type { PublicConfig } from '../lib/env.ts'
import type { Bilingual } from './lang.ts'

/**
 * §5.12: the EN switch turns every page string, this one included.
 *
 * The page's own sentence for a read that did not come back. It is kept as a
 * `{hu, en}` pair rather than as a resolved string so that switching the
 * language while the note is on screen turns it too. A sentence the
 * **server** sent is Hungarian in release one by design, so both halves of
 * the pair carry it unchanged.
 */
export const MENU_UNAVAILABLE: Bilingual = {
  hu: 'A kínálatot most nem tudjuk betölteni. Kérjük, próbálja meg újra.',
  en: 'We cannot load the menu just now. Please try again.',
}

export const serverSentence = (message: string): Bilingual => ({ hu: message, en: message })

export interface ContextReading {
  context: MenuContext | null
  /** The query the context in hand was read for. */
  readFor: string | null
  /** The context is for an older answer than the one on screen. */
  stale: boolean
  loading: boolean
  error: Bilingual | null
  /**
   * Read the menu again for the same answers -- 25 October 2026: the server
   * said the catalogue changed during submission, so the offer on screen is
   * re-priced from a fresh reading. Until it arrives the reading is stale, so
   * the order button waits for it.
   */
  reload: () => void
}

export const queryKey = (query: MenuContextQuery): string =>
  [query.date ?? '', query.start ?? '', query.postcode ?? '', query.fulfilment ?? 'delivery'].join('|')

/** A postcode is only worth asking about once it is four digits (§8.1). */
export const askablePostcode = (postcode: string): string | null =>
  /^\d{4}$/.test(postcode.trim()) ? postcode.trim() : null

/**
 * The debounce exists for one field: the postcode, which changes four times
 * while it is typed. 200 ms is below the threshold at which a customer reads
 * a page as having paused and above the gap between two keystrokes.
 */
const DEBOUNCE_MS = 200

export function useMenuContext(
  config: PublicConfig,
  query: MenuContextQuery,
  fetchImpl: typeof fetch = fetch,
): ContextReading {
  const [nonce, setNonce] = useState(0)
  /* The nonce is part of the key, so a reload is "a newer question than the
     answer in hand" in exactly the way a changed date is. */
  const key = `${queryKey(query)}#${nonce}`
  const [reading, setReading] = useState<{ context: MenuContext | null; readFor: string | null }>({
    context: null,
    readFor: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Bilingual | null>(null)
  /* The last query actually sent, so a re-render with the same answers does
     not ask again and a slow answer to an old query cannot overwrite a new
     one. */
  const inFlight = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      inFlight.current = key
      setLoading(true)
      readMenuContext(config, query, fetchImpl)
        .then((context) => {
          if (cancelled || inFlight.current !== key) return
          setReading({ context, readFor: key })
          setError(null)
          setLoading(false)
        })
        .catch((cause: unknown) => {
          if (cancelled || inFlight.current !== key) return
          setError(
            cause instanceof MenuContextError && cause.serverMessage !== null
              ? serverSentence(cause.serverMessage)
              : MENU_UNAVAILABLE,
          )
          setLoading(false)
        })
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return useMemo(
    () => ({
      context: reading.context,
      readFor: reading.readFor,
      stale: reading.readFor !== null && reading.readFor !== key,
      loading,
      error,
      reload,
    }),
    [reading, key, loading, error, reload],
  )
}
